<template>
  <div>
    <Flex
      justify="between"
      :direction="$screen !== 'l' ? 'column' : 'row'"
    >
      <Flex
        direction="column"
        :align="$screen === 's' ? 'stretch' : 'start'"
      >
        <BaseSwitch
          v-model="selectedFilter"
          :options="options"
          :vertical="$screen === 's'"
          v-test="'products-filter'"
          :label="$t('products.overview_on')"
        />
      </Flex>
      <Flex
        direction="column"
        :align="$screen === 's' ? 'stretch' : 'end'"
      >
        <Flex
          :align="$screen === 's' ? 'stretch' : 'end'"
          :direction="$screen === 's' ? 'column' : 'row'"
        >
          <BaseDropdown
            v-if="productCategories && productCategories.length"
            v-model="selectedCategories"
            :label="$t('global.category')"
            :options="
              productCategories.map((p) => ({
                value: p.id,
                label: p.name
              }))
            "
            searchable
            v-test="'select-category'"
          />
          <BaseDropdown
            v-if="suppliers && suppliers.length"
            v-model="selectedSuppliers"
            :label="$t('global.supplier')"
            :options="
              suppliers.map((s) => ({
                value: s.id,
                label: s.name
              }))
            "
            searchable
            v-test="'select-supplier'"
          />
          <BaseSearch
            v-model="searchQuery"
            debounce
          />
        </Flex>
        <Flex justify="end">
          <BaseButton
            :routerLink="{ name: 'update-product-prices' }"
            color="inverted"
            v-test="'updatePricesButton'"
          >
            {{ $t('products.update_prices') }}
          </BaseButton>
          <BaseButton
            v-if="$screen !== 's'"
            color="inverted"
            :routerLink="{ name: 'import-products' }"
          >
            {{ $t('global.actions.import') }}
          </BaseButton>
          <BaseButton
            :routerLink="{ name: 'create-product' }"
            v-test="'createProduct'"
          >
            {{ $t('global.actions.new_product') }}
          </BaseButton>
        </Flex>
      </Flex>
    </Flex>
  </div>
</template>

<script lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { mapState } from 'pinia';
import { computed, defineComponent } from 'vue';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { GET_PRODUCT_CATEGORIES, GET_SUPPLIERS } from './graphql';

export default defineComponent({
  props: {
    categoryIds: {
      type: Array,
      required: true,
    },
    supplierIds: {
      type: Array,
      required: true,
    },
  },
  emits: [
    'update:categoryIds',
    'update:supplierIds',
    'search',
    'selectedFilter',
  ],
  setup() {
    const { result: categoriesResult } = useQuery(GET_PRODUCT_CATEGORIES, {
      pagination: {
        currentPage: 1,
        perPage: 1000,
      },
    });

    const productCategories = computed(
      () => categoriesResult.value?.productCategories || [],
    );

    const { locationId } = useLocationsStore();

    const { result: suppliersResult } = useQuery(GET_SUPPLIERS, {
      pagination: {
        currentPage: 1,
        perPage: 1000,
      },
      locationId,
    });

    const suppliers = computed(() => suppliersResult.value?.suppliers || []);

    return {
      productCategories,
      suppliers,
    };
  },
  data() {
    return {
      selectedFilter: 'all',
      searchQuery: '',
    };
  },
  watch: {
    searchQuery(value) {
      this.$emit('search', value);
    },
    selectedFilter() {
      const filter = this.selectedFilter !== 'all' ? this.selectedFilter : null;
      this.$emit('selectedFilter', filter);
    },
  },
  computed: {
    ...mapState(useCompanyStore, ['company']),
    ...mapState(useLocationsStore, ['locationId', 'dataScope']),
    selectedCategories: {
      get() {
        return this.categoryIds;
      },
      set(value) {
        this.$emit('update:categoryIds', value);
      },
    },
    selectedSuppliers: {
      get() {
        return this.supplierIds;
      },
      set(value) {
        this.$emit('update:supplierIds', value);
      },
    },
    options() {
      return [
        {
          label: this.$t('products.all_products'),
          value: 'all',
        },
        {
          label: this.$t('products.stock_warnings'),
          value: 'out_of_stock',
          notification: this.stockWarningCount,
        },
        {
          label: this.$t('global.inventory'),
          value: 'stock_enabled',
        },
      ];
    },
    stockWarningCount() {
      if (this.company?.stockWarnings) {
        const warning = this.company.stockWarnings.find(
          (war) => war.location_id === this.locationId,
        );
        if (warning) {
          return warning.count;
        } else {
          return 0;
        }
      }
      return 0;
    },
  },
});
</script>
